import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';

import { Datepicker } from '@mobiscroll/react';
import { Box } from '@mui/material';
import moment from 'moment';

import { CalendarIcon } from '@setup/svg-icons/svg-icons';
import { MuiBaseTextFiled, MuiButton, MuiSelect } from '@shared/ui-kit';
import { datePickerWithPresetsOptions } from '@shared/ui-kit/pickers/DatePickerRangeWithPresets/configs/optionCofig';
import { getSelectedPresent } from '@shared/ui-kit/pickers/DatePickerRangeWithPresets/utils/getSelectedPresent';

import type { MbscDatepickerChangeEvent } from '@mobiscroll/react';
import type { FormSubmitHandlerModel } from '@shared/models/formSubmitHandler.model';
import type { Moment } from 'moment';

type DatePickerPopupContentProps = {
  value: Moment[] | null;
  onChange: (date: Moment[] | null) => void;
  onClose: () => void;
  dateFormat?: string;
};
const DatePickerPopupContent = forwardRef<FormSubmitHandlerModel, DatePickerPopupContentProps>(
  (props, ref) => {
    const { value, onChange, onClose, dateFormat } = props;

    const [selectedDate, setSelectedDate] = useState<Moment[]>(value || []);
    const [start, startRef] = useState<HTMLHeadingElement | null>(null);
    const [end, endRef] = useState<HTMLHeadingElement | null>(null);
    const [selected, setSelected] = useState<{ label: string; value: string } | null>(
      value?.[0] && moment.isMoment(value?.[0]) ? getSelectedPresent(value[0], value[1]) : null,
    );
    const onDateChange = useCallback((ev: MbscDatepickerChangeEvent) => {
      const date = ev.value as any;
      setSelectedDate(date);
      if (date[0]) {
        const startDate = moment(date[0]);
        const endDate = moment(date[1]);
        const selectedPreset = getSelectedPresent(startDate, endDate);
        if (selectedPreset && startDate) {
          setSelected(selectedPreset);
        } else {
          setSelected({ label: 'Custom range', value: 'custom' });
        }
      } else {
        setSelected(null);
      }
    }, []);

    const onChangeSelect = useCallback((event: { label: string; value: string }) => {
      const s = event?.value;

      switch (s) {
        case 'today':
          setSelectedDate([moment(), moment()]);
          break;
        case 'yesterday':
          setSelectedDate([
            moment().subtract(1, 'day').startOf('day'),
            moment().subtract(1, 'day').endOf('day'),
          ]);
          break;
        case 'last-7-days':
          setSelectedDate([moment().subtract(6, 'days').startOf('day'), moment().endOf('day')]);
          break;
        case 'last-14-days':
          setSelectedDate([moment().subtract(13, 'days').startOf('day'), moment().endOf('day')]);
          break;
        case 'this-month':
          setSelectedDate([moment().startOf('month').startOf('day'), moment().endOf('month').endOf('day')]);
          break;
        case 'last-month':
          setSelectedDate([
            moment().subtract(1, 'month').startOf('month').startOf('day'),
            moment().subtract(1, 'month').endOf('month').endOf('day'),
          ]);
          break;
        case 'last-3-months':
          setSelectedDate([
            moment().subtract(2, 'months').startOf('month').startOf('day'),
            moment().endOf('month').endOf('day'),
          ]);
          break;
        case 'last-6-months':
          setSelectedDate([
            moment().subtract(5, 'months').startOf('month').startOf('day'),
            moment().endOf('month').endOf('day'),
          ]);
          break;
        case 'this-year':
          setSelectedDate([moment().startOf('year').startOf('day'), moment().endOf('year').endOf('day')]);
          break;
        case 'last-year':
          setSelectedDate([
            moment().subtract(1, 'year').startOf('year').startOf('day'),
            moment().subtract(1, 'year').endOf('year').endOf('day'),
          ]);
          break;
        default:
          break;
      }

      setSelected(event);
    }, []);

    useImperativeHandle(ref, () => ({
      submit: () => {
        onChange?.(selectedDate);
      },
    }));

    const applyClick = () => {
      onChange?.(selectedDate);
      onClose();
    };

    const cancelClick = () => {
      onClose();
    };

    return (
      <Box display={'flex'} flexDirection={{ xs: 'column-reverse', sm: 'row' }}>
        <Box>
          <Datepicker
            cssClass="date-picker"
            themeVariant="light"
            moment={moment}
            firstDay={1}
            select={'range'}
            focusOnClose={false}
            showRangeLabels={false}
            display="inline"
            max={moment()
              .endOf('year')
              .format(dateFormat || 'MMM DD YYYY')}
            min={moment()
              .subtract(5, 'year')
              .startOf('year')
              .format(dateFormat || 'MMM DD YYYY')}
            startInput={start}
            endInput={end}
            dayNamesMin={['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']}
            controls={['calendar']}
            theme="ios"
            returnFormat="moment"
            dateFormat={dateFormat || 'MMM DD YYYY'}
            touchUi={false}
            value={selectedDate}
            onChange={onDateChange}
          />
          <Box mt={'16px'} display={{ xs: 'flex', sm: 'none' }} justifyContent={'flex-end'}>
            <MuiButton
              size={'medium'}
              variant={'text'}
              label={'Cancel'}
              color={'secondary'}
              onClick={cancelClick}
            />
            <MuiButton
              size={'medium'}
              variant={'text'}
              label={'Done'}
              color={'primary'}
              onClick={applyClick}
            />
          </Box>
        </Box>

        <Box
          ml={{ xs: '0px', sm: '24px' }}
          mb={{ xs: '24px', sm: '0px' }}
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'space-between'}
          width={{ xs: '100%', sm: '184px' }}
        >
          <Box display={'flex'} flexDirection={'column'}>
            <MuiSelect
              isSearchable
              options={datePickerWithPresetsOptions}
              label={'Date range'}
              onChange={onChangeSelect}
              placeholder={'Select'}
              value={selected}
            />

            <Box mt={'16px'}>
              <MuiBaseTextFiled
                variant="outlined"
                required
                placeholder={'Enter'}
                ref={startRef}
                size="medium"
                label={'Start'}
                isCustomHasValue={!!selectedDate?.[0]}
                isClearable
                inputProps={{
                  startAdornment: <CalendarIcon />,
                }}
              />
            </Box>
            <Box mt={'16px'}>
              <MuiBaseTextFiled
                variant="outlined"
                required
                ref={endRef}
                placeholder={'Enter'}
                label={'End'}
                size="medium"
                isCustomHasValue={!!selectedDate?.[1]}
                isClearable
                inputProps={{
                  startAdornment: <CalendarIcon />,
                }}
              />
            </Box>
          </Box>
          <Box display={{ xs: 'none', sm: 'flex' }} justifyContent={'flex-end'} mr={'-10px'}>
            <MuiButton
              size={'small'}
              variant={'text'}
              label={'Cancel'}
              color={'secondary'}
              onClick={cancelClick}
            />
            <MuiButton
              size={'small'}
              variant={'text'}
              label={'Done'}
              color={'primary'}
              onClick={applyClick}
            />
          </Box>
        </Box>
      </Box>
    );
  },
);

export default DatePickerPopupContent;
